import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  // Absolute slide index into the full DOM list (clones + real + clones).
  #currentSlide = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  // Count of real (non-cloned) slides. 0 means clones were not set up.
  #numRealSlides = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.marquee) {
      this.#setupMarquee();
      return;
    }

    this.#setupInfiniteClones();
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    const container = this.querySelector('.announcement-bar__slides');
    if (container && this.#numRealSlides > 0) {
      const ipp = this.itemsPerPage;
      // Position at the first real slide (after prepended clones) without animating.
      container.style.transition = 'none';
      this.#currentSlide = this.#numRealSlides;
      container.style.transform = `translateX(-${(this.#numRealSlides * 100) / ipp}%)`;
      container.getBoundingClientRect(); // force reflow so instant jump applies
      container.style.transition = '';
      this.#updateAriaHidden(0, ipp);
    } else {
      // Single slide — just ensure it is visible.
      this.querySelectorAll('slideshow-slide').forEach((slide, i) => {
        slide.setAttribute('aria-hidden', `${i !== 0}`);
      });
    }

    this.play();
  }

  get marquee() {
    return this.hasAttribute('marquee');
  }

  #setupMarquee() {
    const container = /** @type {HTMLElement|null} */ (this.querySelector('.announcement-bar__slides'));
    if (!container) return;

    const slides = Array.from(container.children);
    if (!slides.length) return;

    // Fill the strip with enough copies to always cover the viewport, then let
    // CSS animate the inner track. No pixel measurement needed.
    const track = document.createElement('div');
    track.className = 'announcement-bar__marquee-track';

    // Four full sets of slides — animation scrolls first two out, second two follow seamlessly.
    [0, 1, 2, 3].forEach(() => {
      const group = document.createElement('div');
      group.className = 'announcement-bar__marquee-group';
      slides.forEach((slide) => {
        group.append(slide.cloneNode(true));
      });
      track.append(group);
    });

    // Replace slides content with the track.
    container.innerHTML = '';
    container.append(track);
    container.classList.add('announcement-bar__slides--marquee');

    const speed = this.autoplayInterval ? this.autoplayInterval / 1000 : 5;
    const pixelsPerSecond = 100 / speed;

    const setDuration = () => {
      // Measure the first group's natural width using getBoundingClientRect —
      // this works even inside overflow:hidden parents.
      const group = /** @type {HTMLElement} */ (track.firstElementChild);
      const groupWidth = group.getBoundingClientRect().width;
      // 4 groups total; animation scrolls -25% (one group width)
      track.style.setProperty('--marquee-duration', `${groupWidth / pixelsPerSecond}s`);
    };

    document.fonts.ready.then(() => requestAnimationFrame(setDuration));

    document.addEventListener('visibilitychange', () => {
      track.style.animationPlayState = document.hidden ? 'paused' : 'running';
    });
  }

  // Build the DOM:  [N prepended clones]  [N real slides]  [N appended clones]
  // Each step advances by 1 slide so e.g. with 3 visible: [1,2,3]→[2,3,4]→[3,4,1]→…
  #setupInfiniteClones() {
    const container = this.querySelector('.announcement-bar__slides');
    if (!container) return;

    const slides = Array.from(container.children);
    if (slides.length <= this.itemsPerPage) return;

    this.#numRealSlides = slides.length;

    // Prepend in reverse-iteration order so the clone DOM order matches the
    // real slide order (each prepend inserts at position 0).
    [...slides].reverse().forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.clone = 'true';
      clone.querySelectorAll('slideshow-slide').forEach((el) => el.setAttribute('aria-hidden', 'true'));
      container.prepend(clone);
    });

    // Append in normal order.
    slides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.clone = 'true';
      clone.querySelectorAll('slideshow-slide').forEach((el) => el.setAttribute('aria-hidden', 'true'));
      container.append(clone);
    });
  }

  // Mark the ipp real slides starting at realSlideStart (mod N) as visible.
  #updateAriaHidden(realSlideStart, ipp) {
    const n = this.#numRealSlides;
    this.querySelectorAll('.announcement-bar__slide:not([data-clone]) slideshow-slide').forEach(
      (slide, index) => {
        let visible = false;
        for (let i = 0; i < ipp; i++) {
          if (index === (realSlideStart + i) % n) {
            visible = true;
            break;
          }
        }
        slide.setAttribute('aria-hidden', `${!visible}`);
      }
    );
  }

  next() {
    this.currentPage += 1;
  }

  previous() {
    this.currentPage -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get itemsPerPage() {
    const style = getComputedStyle(this.closest('.announcement-bar'));
    const desktop = parseInt(style.getPropertyValue('--desktop-items').trim(), 10) || 1;
    const tablet = parseInt(style.getPropertyValue('--tablet-items').trim(), 10) || 1;
    const mobile = parseInt(style.getPropertyValue('--mobile-items').trim(), 10) || 1;

    if (window.matchMedia('(min-width: 990px)').matches) {
      return desktop;
    }
    if (window.matchMedia('(min-width: 750px)').matches) {
      return tablet;
    }
    return mobile;
  }

  // currentPage stores the absolute slide index (includes clone offset).
  get currentPage() {
    return this.#currentSlide;
  }

  set currentPage(slideIndex) {
    if (this.#numRealSlides === 0) return;

    const N = this.#numRealSlides;
    const ipp = this.itemsPerPage;
    if (ipp === 0) return;

    this.#currentSlide = slideIndex;

    const container = this.querySelector('.announcement-bar__slides');
    if (container) {
      // Each slide is (100/ipp)% of the viewport; moving 1 slide = (100/ipp)%.
      container.style.transform = `translateX(-${(slideIndex * 100) / ipp}%)`;
    }

    // Determine which real slide sits at the left edge of the visible window.
    let realStart = (slideIndex - N) % N;
    if (realStart < 0) realStart += N;
    this.#updateAriaHidden(realStart, ipp);

    // When the transition lands fully in a clone zone, silently snap back to
    // the visually identical position in the real zone.
    const inCloneZone = slideIndex >= 2 * N || slideIndex < N;
    if (inCloneZone && container) {
      const onEnd = () => {
        container.removeEventListener('transitionend', onEnd);
        container.style.transition = 'none';

        if (this.#currentSlide >= 2 * N) {
          this.#currentSlide -= N;
        } else if (this.#currentSlide < N) {
          this.#currentSlide += N;
        }

        container.style.transform = `translateX(-${(this.#currentSlide * 100) / ipp}%)`;
        container.getBoundingClientRect(); // force reflow before restoring transition
        container.style.transition = '';
      };
      container.addEventListener('transitionend', onEnd);
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
